'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import StickerCard from '@/components/public/sticker-card';
import { Circle, Eyebrow, PatternPanel, toneAt, toneFill } from '@/components/public/decor';
import { getPlanCatalog } from '@/lib/billing/api';
import { formatLimit, formatMoney } from '@/lib/billing/types';
import type { Plan } from '@/lib/billing/types';

const popIn = {
  initial: { opacity: 0, y: 24, scale: 0.96 },
  whileInView: { opacity: 1, y: 0, scale: 1 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] as const },
};

/**
 * Compact three-card pricing summary for marketing pages. Prices come from the
 * same backend catalog as the pricing page, so the homepage can never drift.
 */
export default function PricingTeaser() {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [trialDays, setTrialDays] = useState(7);

  useEffect(() => {
    getPlanCatalog()
      .then((catalog) => {
        setPlans(catalog.plans.filter((plan) => plan.selfServe));
        setTrialDays(catalog.trial.days);
      })
      .catch((error) => console.error('Could not load plans:', error));
  }, []);

  return (
    <section className="relative overflow-hidden py-24">
      <PatternPanel pattern="dots" className="inset-x-0 top-0 h-40 opacity-60" />
      <Circle tone="mint" className="-left-20 bottom-16 h-52 w-52 opacity-25" />

      <div className="container relative max-w-5xl px-4">
        <motion.div {...popIn} className="mx-auto max-w-2xl text-center">
          <Eyebrow tone="amber" className="justify-center">
            Pricing
          </Eyebrow>
          <h2 className="mt-4 font-outfit text-3xl font-extrabold sm:text-4xl">
            Priced by fleet, not by seat
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {trialDays} days free at full capability. Unlimited dispatchers on every plan.
          </p>
        </motion.div>

        {!plans ? (
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            <Skeleton className="h-80 rounded-2xl" />
            <Skeleton className="h-80 rounded-2xl" />
            <Skeleton className="h-80 rounded-2xl" />
          </div>
        ) : (
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {plans.map((plan, i) => (
              <motion.div key={plan.id} {...popIn} transition={{ ...popIn.transition, delay: i * 0.08 }}>
                <StickerCard
                  interactive
                  shadow={plan.recommended ? 'accent' : 'soft'}
                  className={`relative flex h-full flex-col p-6 ${plan.recommended ? 'bg-pop-ink text-white' : ''}`}
                >
                  {plan.recommended && (
                    <span className="absolute -top-3 right-4 rotate-6 rounded-full border-2 border-pop-ink bg-pop-amber px-3 py-1 text-[10px] font-extrabold uppercase tracking-wide text-pop-ink">
                      Popular
                    </span>
                  )}

                  <p
                    className={`text-xs font-extrabold uppercase tracking-wider ${
                      plan.recommended ? 'text-pop-amber' : 'text-muted-foreground'
                    }`}
                  >
                    {plan.name}
                  </p>

                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-outfit text-4xl font-extrabold">
                      {formatMoney(plan.price.monthly ?? 0)}
                    </span>
                    <span className={`text-sm ${plan.recommended ? 'text-white/60' : 'text-muted-foreground'}`}>
                      /mo
                    </span>
                  </div>

                  <p className={`mt-1 text-sm ${plan.recommended ? 'text-white/70' : 'text-muted-foreground'}`}>
                    {plan.tagline}
                  </p>

                  <ul className="mt-6 flex-1 space-y-3">
                    {[
                      `${formatLimit(plan.limits.drivers)} drivers`,
                      `${formatLimit(plan.limits.tasksPerMonth)} tasks / month`,
                      `${plan.limits.pingIntervalSeconds}s location pings`,
                    ].map((feature) => (
                      <li key={feature} className="flex items-center gap-2.5 text-sm font-medium">
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${toneFill(toneAt(i))}`}
                        >
                          <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={plan.recommended ? 'candy' : 'candy-outline'}
                    size="pill"
                    className={`mt-6 w-full ${plan.recommended ? '' : ''}`}
                    asChild
                  >
                    <Link href="/pricing">Start free trial</Link>
                  </Button>
                </StickerCard>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            href="/pricing"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-pop-ink px-5 py-2.5 text-sm font-bold transition-colors hover:bg-pop-amber"
          >
            Compare every plan, including Enterprise
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
